/* ==========================================================================
   Seção "Inspire-se na nossa comunidade" (influencer-community)
   --------------------------------------------------------------------------
   Custom element <cdr-influencer-community> que monta um Swiper com efeito
   Coverflow 3D.

   Decisões importantes:
   - A biblioteca Swiper é carregada UMA VEZ por página (promise única em
     window.__cdrSwiperLoader). Se o tema já expõe window.Swiper, reutiliza.
   - Ser custom element resolve o editor de tema: connectedCallback monta e
     disconnectedCallback destrói, então recarregar/mover a seção nunca
     empilha instância, autoplay ou listener.
   - Também escutamos shopify:section:load / :unload / block:select para
     atualizar sem recarregar a página (idempotente: se já há instância, só
     dá update em vez de recriar).
   - prefers-reduced-motion desliga o autoplay.
   ========================================================================== */

(function () {
  'use strict';

  // Trava de execução única: se duas instâncias da seção emitirem a tag
  // <script>, o arquivo roda duas vezes e customElements.define quebraria.
  if (window.CdrInfluencerCommunity) return;

  var TAG = 'cdr-influencer-community';

  /* ----------------------------------------------------------------------
     Carregamento único da biblioteca
     ---------------------------------------------------------------------- */
  function loadSwiper(src) {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (window.__cdrSwiperLoader) return window.__cdrSwiperLoader;
    if (!src) return Promise.reject(new Error('influencer-community: caminho do Swiper ausente'));

    window.__cdrSwiperLoader = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-cdr-swiper', '');
      script.onload = function () {
        if (window.Swiper) resolve(window.Swiper);
        else reject(new Error('influencer-community: Swiper carregou mas não expôs window.Swiper'));
      };
      script.onerror = function () {
        window.__cdrSwiperLoader = null;
        reject(new Error('influencer-community: falha ao carregar ' + src));
      };
      document.head.appendChild(script);
    });

    return window.__cdrSwiperLoader;
  }

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* ----------------------------------------------------------------------
     Elemento
     ---------------------------------------------------------------------- */
  class CdrInfluencerCommunity extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.token = 0;
      this.retries = 0;
      this.originalCount = 0;
      this.autoplayEnabled = false;
    }

    connectedCallback() {
      this.mount();
    }

    disconnectedCallback() {
      this.destroy();
    }

    get config() {
      if (this._config) return this._config;
      var node = this.querySelector('[data-cdr-ic-config]');
      try {
        this._config = node ? JSON.parse(node.textContent) : {};
      } catch (e) {
        console.warn('influencer-community: config inválida', e);
        this._config = {};
      }
      return this._config;
    }

    get track() {
      return this.querySelector('.cdr-ic__track');
    }

    /* Monta (ou remonta) o carrossel. É idempotente: destrói o anterior. */
    mount() {
      var track = this.track;
      if (!track) {
        // defesa contra upgrade do elemento antes dos filhos existirem
        if (this.retries++ < 3) requestAnimationFrame(this.mount.bind(this));
        return;
      }
      this.retries = 0;
      this.destroy();

      var self = this;
      var cfg = this.config;
      var myToken = ++this.token;

      loadSwiper(this.dataset.swiperSrc)
        .then(function (Swiper) {
          // seção pode ter sido removida/remontada enquanto a lib carregava
          if (!self.isConnected || myToken !== self.token) return;
          self.buildSlides(cfg);
          self.swiper = new Swiper(self.querySelector('.cdr-ic__slider'), self.options(cfg));
        })
        .catch(function (err) {
          console.warn(err && err.message ? err.message : err);
        });
    }

    destroy() {
      this.token++;
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {
          /* instância já destruída */
        }
        this.swiper = null;
      }
      this.removeReplicas();
    }

    /* ------------------------------------------------------------------
       Loop de verdade precisa de material: com poucas fotos o coverflow
       abriria buracos nas pontas. Replicamos os slides originais (marcados,
       fora da árvore de acessibilidade e fora da ordem de tabulação) até
       cobrir o dobro do que cabe na tela.

       O total SEMPRE fecha em múltiplo do conjunto original: se sobrasse
       um pedaço (9 fotos completadas até 12, por exemplo), as 3 primeiras
       apareceriam duas vezes por volta e o giro ficaria desigual.
       ------------------------------------------------------------------ */
    buildSlides(cfg) {
      var track = this.track;
      if (!track) return;

      var originals = Array.prototype.filter.call(track.children, function (el) {
        return el.classList.contains('cdr-ic__slide') && !el.hasAttribute('data-cdr-replica');
      });
      this.originalCount = originals.length;
      if (!originals.length) return;

      var perView = Math.max(
        Number(cfg.desktopPerView) || 4,
        2,
        Number(cfg.tabletPerView) || 2
      );
      var minimum = Math.max(8, Math.ceil(perView) * 2 + 4);
      // arredonda para cima até fechar voltas inteiras do conjunto
      minimum = Math.ceil(minimum / originals.length) * originals.length;
      if (originals.length >= minimum) return;

      var frag = document.createDocumentFragment();
      var total = originals.length;
      var i = 0;
      while (total < minimum) {
        var clone = originals[i % originals.length].cloneNode(true);
        clone.setAttribute('data-cdr-replica', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        // some do editor: o bloco original continua sendo o selecionável
        clone.removeAttribute('data-shopify-editor-block');
        Array.prototype.forEach.call(clone.querySelectorAll('a'), function (a) {
          a.setAttribute('tabindex', '-1');
        });
        frag.appendChild(clone);
        total++;
        i++;
      }
      track.appendChild(frag);
    }

    removeReplicas() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-cdr-replica]'), function (el) {
        el.remove();
      });
    }

    /* ------------------------------------------------------------------
       Configuração do Swiper (valores da referência XO Gang)
       ------------------------------------------------------------------ */
    options(cfg) {
      var reduced = prefersReducedMotion();
      // guarda se o autoplay foi de fato ligado: o Swiper 11 sempre cria
      // swiper.autoplay e o start() dele NÃO checa params.autoplay.enabled
      this.autoplayEnabled = !!(cfg.autoplay && !reduced);

      var options = {
        effect: 'coverflow',
        centeredSlides: true,
        slidesPerView: 'auto',
        spaceBetween: 0,
        loop: true,
        speed: Number(cfg.speed) || 600,
        initialSlide: 1,
        loopAdditionalSlides: 4,
        loopedSlides: 4, // compatibilidade com Swiper < 9
        grabCursor: true,
        watchSlidesProgress: true,
        coverflowEffect: {
  rotate: 0,
  stretch: window.innerWidth <= 749 ? 110 : 0,
  depth: window.innerWidth <= 749 ? 160 : cfg.depth,
  modifier: 1,
  scale: window.innerWidth <= 749 ? 0.82 : 1,
  slideShadows: false
},
breakpoints: {
          320: { slidesPerView: Number(cfg.mobilePerView) || 1.3 },
          768: { slidesPerView: Number(cfg.tabletPerView) || 2 },
          1024: { slidesPerView: Number(cfg.desktopPerView) || 4 }
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Foto anterior',
          nextSlideMessage: 'Próxima foto',
          firstSlideMessage: 'Primeira foto',
          lastSlideMessage: 'Última foto'
        },
        on: {
          // o coverflow deixa slides fora da tela clicáveis; some com eles
          // do teclado para o Tab não "sumir" atrás das fotos vizinhas.
          // Precisa rodar também em resize/breakpoint/loopFix: o Swiper
          // recalcula quais slides estão visíveis nesses casos sem trocar
          // de slide, então só slideChange deixaria o tabindex desatualizado.
          init: this.afterRender.bind(this),
          slideChangeTransitionEnd: this.syncFocusables.bind(this),
          transitionEnd: this.syncFocusables.bind(this),
          resize: this.syncFocusables.bind(this),
          breakpoint: this.syncFocusables.bind(this),
          loopFix: this.syncFocusables.bind(this),
          paginationRender: this.syncPagination.bind(this),
          paginationUpdate: this.syncPagination.bind(this)
        }
      };

      if (cfg.autoplay && !reduced) {
        options.autoplay = {
          delay: Number(cfg.autoplayDelay) || 2000,
          disableOnInteraction: false,
          pauseOnMouseEnter: !!cfg.pauseOnHover
        };
      }

      if (cfg.arrows) {
        options.navigation = {
          nextEl: this.querySelector('.cdr-ic__arrow--next'),
          prevEl: this.querySelector('.cdr-ic__arrow--prev'),
          disabledClass: 'cdr-ic__arrow--noop' // loop infinito: nunca desabilita
        };
      }

      if (cfg.pagination) {
        options.pagination = {
          el: this.querySelector('.cdr-ic__pagination'),
          clickable: true
          // sem dynamicBullets de propósito: esse modo grava width inline no
          // elemento de paginação e tira as bolinhas do centro
        };
      }

      return options;
    }

    afterRender() {
      this.syncFocusables();
      this.syncPagination();
    }

    /* Réplicas não são fotos novas: a bolinha delas some da paginação. */
    syncPagination() {
      var pag = this.querySelector('.cdr-ic__pagination');
      if (!pag || !this.originalCount) return;
      var bullets = pag.querySelectorAll('.swiper-pagination-bullet');
      var limite = this.originalCount;
      Array.prototype.forEach.call(bullets, function (b, i) {
        b.classList.toggle('cdr-ic__bullet--replica', i >= limite);
      });
    }

    /* Mantém no Tab só o que está visível de fato. */
    syncFocusables() {
      var slides = this.querySelectorAll('.cdr-ic__slide');
      Array.prototype.forEach.call(slides, function (slide) {
        var hidden = slide.hasAttribute('data-cdr-replica') || !slide.classList.contains('swiper-slide-visible');
        Array.prototype.forEach.call(slide.querySelectorAll('a'), function (a) {
          if (hidden) a.setAttribute('tabindex', '-1');
          else a.removeAttribute('tabindex');
        });
      });
    }

    /* Usado pelo editor ao selecionar um bloco. */
    goToBlock(slide) {
      if (!this.swiper) return;
      var index = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
      if (isNaN(index)) return;
      if (this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
      this.swiper.slideToLoop(index);
    }

    resumeAutoplay() {
      if (!this.swiper || !this.swiper.autoplay) return;
      // sem esta guarda o autoplay ligaria mesmo com o checkbox desmarcado ou
      // com movimento reduzido: o start() do Swiper não olha o enabled
      var p = this.swiper.params && this.swiper.params.autoplay;
      if (!p || !p.enabled || !this.autoplayEnabled) return;
      if (!this.swiper.autoplay.running) this.swiper.autoplay.start();
    }
  }

  window.CdrInfluencerCommunity = CdrInfluencerCommunity;
  if (!customElements.get(TAG)) customElements.define(TAG, CdrInfluencerCommunity);

  /* ----------------------------------------------------------------------
     Integração com o editor de tema
     ---------------------------------------------------------------------- */
  function each(root, fn) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    Array.prototype.forEach.call(root.querySelectorAll(TAG), fn);
    if (root.matches && root.matches(TAG)) fn(root);
  }

  document.addEventListener('shopify:section:load', function (event) {
    each(event.target, function (el) {
      // connectedCallback já montou; aqui só garantimos o estado correto
      if (el.swiper) el.swiper.update();
      else el.mount();
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    each(event.target, function (el) {
      el.destroy();
    });
  });

  document.addEventListener('shopify:section:select', function (event) {
    each(event.target, function (el) {
      if (el.swiper && el.swiper.autoplay) el.swiper.autoplay.stop();
    });
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    each(event.target, function (el) {
      el.resumeAutoplay();
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest ? event.target.closest('.cdr-ic__slide') : null;
    if (!slide) return;
    var host = slide.closest(TAG);
    if (host) host.goToBlock(slide);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var slide = event.target.closest ? event.target.closest('.cdr-ic__slide') : null;
    if (!slide) return;
    var host = slide.closest(TAG);
    if (host) host.resumeAutoplay();
  });
})();
